import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CallMadeIcon from "@mui/icons-material/CallMade";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import CancelIcon from "@mui/icons-material/Cancel";
import SendIcon from "@mui/icons-material/Send";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import DrawerComponentView from "src/app/components/drawer/drawer.view";
import DrawerAwareContainerComponentView from "src/app/components/drawerAwareContainer/drawerAwareContainer.view";
import SnackBarComponentView from "src/app/components/snackbar/snackbar.view";
import {
  AutomaticPayment,
  Payment,
  PaymentPeriod,
  PaymentUser,
  UpcomingPayment,
} from "src/app/models/screens/payments.model";
import PaymentsScreenViewModel from "src/app/screens/payments/payments.viewmodel";
import styles from "src/app/styles/screens/payments.style";

const PERIOD_OPTIONS: { value: PaymentPeriod; label: string }[] = [
  { value: "past_week", label: "Past Week" },
  { value: "past_month", label: "Past Month" },
  { value: "past_quarter", label: "Past Quarter" },
  { value: "past_year", label: "Past Year" },
  { value: "all_time", label: "All Time" },
];

const PeriodSelector = ({
  value,
  onChange,
}: {
  value: PaymentPeriod;
  onChange: (p: PaymentPeriod) => void;
}) => (
  <Box sx={styles.periodSelectorContainer}>
    {PERIOD_OPTIONS.map((opt) => (
      <Chip
        key={opt.value}
        label={opt.label}
        clickable
        color={value === opt.value ? "primary" : "default"}
        onClick={() => onChange(opt.value)}
        size="small"
      />
    ))}
  </Box>
);

const PaymentCard = ({
  payment,
  showSender,
  formatCurrency,
}: {
  payment: Payment;
  showSender: boolean;
  formatCurrency: (n: number) => string;
}) => (
  <Paper elevation={0} sx={styles.selectedTabContent}>
    <Box sx={styles.paymentCardHeader}>
      <Typography sx={styles.paymentCardName}>
        {showSender ? payment.senderFullName : payment.recipientFullName}
      </Typography>
      <Typography sx={styles.paymentCardAmount}>
        {formatCurrency(payment.amount)}
      </Typography>
    </Box>
    <Typography sx={styles.paymentCardMeta}>Date: {payment.date}</Typography>
    <Typography sx={styles.paymentCardDescription}>
      {payment.description}
    </Typography>
  </Paper>
);

const PaymentsListSection = ({
  payments,
  isLoading,
  period,
  onPeriodChanged,
  total,
  chartData,
  showSender,
  formatCurrency,
  periodLabel,
}: {
  payments: Payment[];
  isLoading: boolean;
  period: PaymentPeriod;
  onPeriodChanged: (p: PaymentPeriod) => void;
  total: number;
  chartData: { label: string; amount: number }[];
  showSender: boolean;
  formatCurrency: (n: number) => string;
  periodLabel: (p: PaymentPeriod) => string;
}) => (
  <>
    <PeriodSelector value={period} onChange={onPeriodChanged} />
    {isLoading ? (
      <Box sx={styles.searchingLoader}>
        <CircularProgress size={28} />
      </Box>
    ) : (
      <>
        {chartData.length > 0 && (
          <Paper elevation={0} sx={styles.chartContainer}>
            <Typography sx={styles.chartTitle}>
              {periodLabel(period)} — payments over time
            </Typography>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart
                data={chartData}
                margin={{ top: 4, right: 8, left: 0, bottom: 4 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(0,0,0,0.06)"
                />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => `£${v}`}
                />
                <Tooltip
                  formatter={(v) => [`£${(v as number).toFixed(2)}`, "Amount"]}
                />
                <Bar dataKey="amount" fill="#1976d2" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        )}
        <Box sx={styles.summaryRow}>
          <Typography sx={styles.selectedTabText}>
            Transactions ({payments.length})
          </Typography>
          <Typography sx={styles.totalText}>
            Total: {formatCurrency(total)}
          </Typography>
        </Box>
        {payments.length === 0 ? (
          <Typography sx={styles.noItemsText}>
            No payments for this period.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {payments.map((payment: Payment) => (
              <Grid key={payment.id} size={{ xs: 12, md: 6 }}>
                <PaymentCard
                  payment={payment}
                  showSender={showSender}
                  formatCurrency={formatCurrency}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </>
    )}
  </>
);

const PaymentsScreenView: React.FC = () => {
  const {
    isSnackbarError,
    snackbarMessage,
    isDisplayingSnackbar,
    setIsDisplayingSnackbar,
    selectedTab,
    onTabChanged,
    userRole,
    children,
    isLoadingChildren,
    adminPaymentUsers,
    isLoadingAdminPaymentUsers,
    selectedOwnerId,
    onSelectedOwnerChanged,
    filteredReceivedPayments,
    isLoadingPayments,
    receivedPeriod,
    onReceivedPeriodChanged,
    receivedTotal,
    receivedChartData,
    filteredSentPayments,
    sentPeriod,
    onSentPeriodChanged,
    sentTotal,
    sentChartData,
    automaticPayments,
    isLoadingAutomaticPayments,
    upcomingPayments,
    isLoadingUpcomingPayments,
    onCancelAutomaticPayment,
    cancellingAutomaticPaymentId,
    composeUserSearchText,
    setComposeUserSearchText,
    isSearchingComposeUsers,
    composeSearchedUsers,
    composeSelectedUser,
    onComposeUserSelected,
    composeAmount,
    setComposeAmount,
    composeDate,
    setComposeDate,
    composeDescription,
    setComposeDescription,
    composeIsAutomatic,
    onComposeIsAutomaticChanged,
    composeIntervalDays,
    setComposeIntervalDays,
    isComposingPayment,
    onSubmitPayment,
    formatCurrency,
    periodLabel,
  } = PaymentsScreenViewModel();

  const showOwnerSelectorOnReceivedTab =
    (userRole === "Parent" && selectedTab === 0) ||
    (userRole === "Admin" && selectedTab === 0);

  const showOwnerSelectorOnSentTab = userRole === "Admin" && selectedTab === 1;

  const showOwnerSelector =
    showOwnerSelectorOnReceivedTab || showOwnerSelectorOnSentTab;

  return (
    <Box sx={styles.parent}>
      <DrawerComponentView selectedId="Payments" />
      <DrawerAwareContainerComponentView
        drawerWidth={250}
        sx={styles.drawerAwareContainer}
      >
        <Grid container justifyContent="center" spacing={3}>
          <Grid
            size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}
            sx={styles.titleGrid}
          >
            <Typography component="span" sx={styles.titleText}>
              Payments
            </Typography>
          </Grid>
          {showOwnerSelector && (
            <Grid size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}>
              <Grid
                size={{ xl: 4, lg: 5, md: 5, sm: 6, xs: 7 }}
                sx={styles.selectorContainer}
                justifySelf="flex-end"
              >
                <Paper elevation={0} sx={styles.selectorPaper}>
                  <FormControl fullWidth>
                    <InputLabel id="payment-owner-select-label">
                      {userRole === "Parent"
                        ? "Payment Owner"
                        : "Viewing Payments Of"}
                    </InputLabel>
                    <Select
                      labelId="payment-owner-select-label"
                      value={selectedOwnerId}
                      label={
                        userRole === "Parent"
                          ? "Payment Owner"
                          : "Viewing Payments Of"
                      }
                      onChange={(event: SelectChangeEvent) =>
                        onSelectedOwnerChanged(event.target.value)
                      }
                      disabled={
                        userRole === "Parent"
                          ? isLoadingChildren
                          : isLoadingAdminPaymentUsers
                      }
                    >
                      <MenuItem value="you">You</MenuItem>
                      {userRole === "Parent" &&
                        children.map((child) => (
                          <MenuItem key={child._id} value={child._id}>
                            {child.firstName}
                          </MenuItem>
                        ))}
                      {userRole === "Admin" &&
                        adminPaymentUsers.map((user: PaymentUser) => (
                          <MenuItem key={user.id} value={user.id}>
                            {`${user.firstName} ${user.lastName}`}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Paper>
              </Grid>
            </Grid>
          )}
          <Grid size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}>
            <Paper elevation={0} sx={styles.tabsContainer}>
              {userRole === "Admin" && (
                <>
                  <Tabs
                    value={selectedTab}
                    onChange={onTabChanged}
                    variant="fullWidth"
                    scrollButtons="auto"
                  >
                    <Tab
                      icon={<CallReceivedIcon />}
                      iconPosition="start"
                      label="Received Payments"
                    />
                    {userRole === "Admin" && (
                      <Tab
                        icon={<CallMadeIcon />}
                        iconPosition="start"
                        label="Sent Payments"
                      />
                    )}
                    {userRole === "Admin" && (
                      <Tab
                        icon={<AutorenewIcon />}
                        iconPosition="start"
                        label="Automatic Payments"
                      />
                    )}
                    {userRole === "Admin" && (
                      <Tab
                        icon={<AccountBalanceWalletIcon />}
                        iconPosition="start"
                        label="Compose Payment"
                      />
                    )}
                  </Tabs>
                  <Divider />
                </>
              )}
              {selectedTab === 0 && (
                <Box sx={styles.selectedTab}>
                  <Typography sx={styles.selectedTabText}>
                    Received Payments
                  </Typography>
                  <PaymentsListSection
                    payments={filteredReceivedPayments}
                    isLoading={isLoadingPayments}
                    period={receivedPeriod}
                    onPeriodChanged={onReceivedPeriodChanged}
                    total={receivedTotal}
                    chartData={receivedChartData}
                    showSender={true}
                    formatCurrency={formatCurrency}
                    periodLabel={periodLabel}
                  />
                </Box>
              )}
              {selectedTab === 1 && userRole === "Admin" && (
                <Box sx={styles.selectedTab}>
                  <Typography sx={styles.selectedTabText}>
                    Sent Payments
                  </Typography>
                  <PaymentsListSection
                    payments={filteredSentPayments}
                    isLoading={isLoadingPayments}
                    period={sentPeriod}
                    onPeriodChanged={onSentPeriodChanged}
                    total={sentTotal}
                    chartData={sentChartData}
                    showSender={false}
                    formatCurrency={formatCurrency}
                    periodLabel={periodLabel}
                  />
                </Box>
              )}
              {selectedTab === 2 && userRole === "Admin" && (
                <Box sx={styles.selectedTab}>
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, lg: 6 }}>
                      <Typography sx={styles.sectionTitle}>
                        Active Automatic Payments
                      </Typography>
                      {isLoadingAutomaticPayments ? (
                        <Box sx={styles.searchingLoader}>
                          <CircularProgress size={28} />
                        </Box>
                      ) : automaticPayments.filter(
                          (ap) => ap.status === "Active",
                        ).length === 0 ? (
                        <Typography sx={styles.noItemsText}>
                          No active automatic payments.
                        </Typography>
                      ) : (
                        <Grid container spacing={2}>
                          {automaticPayments
                            .filter(
                              (ap: AutomaticPayment) => ap.status === "Active",
                            )
                            .map((ap: AutomaticPayment) => (
                              <Grid key={ap.id} size={{ xs: 12 }}>
                                <Paper
                                  elevation={0}
                                  sx={styles.automaticPaymentCard}
                                >
                                  <Box sx={styles.automaticPaymentCardHeader}>
                                    <Box>
                                      <Typography sx={styles.paymentCardName}>
                                        {ap.recipientFullName}
                                      </Typography>
                                      <Typography sx={styles.paymentCardMeta}>
                                        {formatCurrency(ap.amount)} every{" "}
                                        {ap.intervalDays} day
                                        {ap.intervalDays !== 1 ? "s" : ""}
                                      </Typography>
                                      <Typography
                                        sx={styles.paymentCardMeta}
                                      ></Typography>
                                      <Typography
                                        sx={styles.paymentCardDescription}
                                      >
                                        {ap.description}
                                      </Typography>
                                    </Box>
                                    <Button
                                      variant="outlined"
                                      color="error"
                                      size="small"
                                      startIcon={<CancelIcon />}
                                      onClick={() =>
                                        onCancelAutomaticPayment(ap.id)
                                      }
                                      loading={
                                        cancellingAutomaticPaymentId === ap.id
                                      }
                                    >
                                      Cancel
                                    </Button>
                                  </Box>
                                </Paper>
                              </Grid>
                            ))}
                        </Grid>
                      )}
                    </Grid>
                    <Grid size={{ xs: 12, lg: 6 }}>
                      <Typography sx={styles.sectionTitle}>
                        Upcoming Payments (Next 30 Days)
                      </Typography>
                      {isLoadingUpcomingPayments ? (
                        <Box sx={styles.searchingLoader}>
                          <CircularProgress size={28} />
                        </Box>
                      ) : upcomingPayments.length === 0 ? (
                        <Typography sx={styles.noItemsText}>
                          No upcoming payments in the next 30 days.
                        </Typography>
                      ) : (
                        <Paper elevation={0} sx={styles.selectedTabContent}>
                          {upcomingPayments.map(
                            (up: UpcomingPayment, index: number) => (
                              <Box
                                key={up.id}
                                sx={[
                                  styles.upcomingPaymentRow,
                                  index === upcomingPayments.length - 1
                                    ? { borderBottom: "none" }
                                    : {},
                                ]}
                              >
                                <Box>
                                  <Typography sx={styles.paymentCardName}>
                                    {up.recipientFullName}
                                  </Typography>
                                  <Typography sx={styles.paymentCardMeta}>
                                    {up.date} — {up.description}
                                  </Typography>
                                </Box>
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  gap={1}
                                  flexWrap="wrap"
                                >
                                  <Typography sx={styles.paymentCardAmount}>
                                    {formatCurrency(up.amount)}
                                  </Typography>
                                  <Chip
                                    size="small"
                                    label={
                                      up.daysUntilDue === 0
                                        ? "Today"
                                        : `${up.daysUntilDue}d`
                                    }
                                    color={
                                      up.daysUntilDue === 0
                                        ? "error"
                                        : up.daysUntilDue <= 3
                                          ? "warning"
                                          : "default"
                                    }
                                    sx={styles.upcomingBadge}
                                  />
                                </Box>
                              </Box>
                            ),
                          )}
                        </Paper>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              )}
              {selectedTab === 3 && userRole === "Admin" && (
                <Box sx={styles.selectedTab}>
                  <Typography sx={styles.selectedTabText}>
                    Compose Payment
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Paper elevation={0} sx={styles.selectedTabContent}>
                        <Typography sx={styles.selectedTabText}>
                          Select Recipient
                        </Typography>
                        <TextField
                          fullWidth
                          label="Search users"
                          value={composeUserSearchText}
                          onChange={(e) =>
                            setComposeUserSearchText(e.target.value)
                          }
                          sx={styles.searchBox}
                        />
                        {isSearchingComposeUsers ? (
                          <Box sx={styles.searchingLoader}>
                            <CircularProgress size={28} />
                          </Box>
                        ) : (
                          <List sx={styles.usersListParent}>
                            {composeSearchedUsers.map((user: PaymentUser) => {
                              const isSelected =
                                composeSelectedUser?.id === user.id;
                              return (
                                <ListItemButton
                                  key={user.id}
                                  onClick={() => onComposeUserSelected(user)}
                                  sx={[
                                    styles.usersListItem,
                                    {
                                      backgroundColor: isSelected
                                        ? "rgba(25,118,210,0.08)"
                                        : "transparent",
                                    },
                                  ]}
                                >
                                  <ListItemText
                                    primary={`${user.firstName} ${user.lastName}`}
                                  />
                                </ListItemButton>
                              );
                            })}
                            {composeSearchedUsers.length === 0 && (
                              <Typography sx={styles.noItemsText}>
                                No users found.
                              </Typography>
                            )}
                          </List>
                        )}
                      </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, md: 8 }}>
                      <Paper elevation={0} sx={styles.composeFormContainer}>
                        <Typography sx={styles.selectedTabText}>
                          Payment Details
                          {composeSelectedUser
                            ? ` — ${composeSelectedUser.firstName} ${composeSelectedUser.lastName}`
                            : ""}
                        </Typography>

                        <TextField
                          fullWidth
                          label="Amount (GBP)"
                          value={composeAmount}
                          onChange={(e) => setComposeAmount(e.target.value)}
                          type="number"
                          inputProps={{ min: 0, step: "0.01" }}
                          sx={styles.composeFormField}
                          InputProps={{
                            startAdornment: (
                              <Typography sx={{ mr: 0.5, opacity: 0.6 }}>
                                £
                              </Typography>
                            ),
                          }}
                        />

                        <DatePicker
                          label="Payment Date"
                          value={composeDate}
                          onChange={(date) => setComposeDate(date)}
                          maxDate={dayjs()}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              sx: styles.composeFormField,
                            },
                          }}
                          disableFuture
                        />

                        <TextField
                          fullWidth
                          multiline
                          minRows={3}
                          label="What is this payment for?"
                          value={composeDescription}
                          onChange={(e) =>
                            setComposeDescription(e.target.value)
                          }
                          sx={styles.composeFormField}
                        />

                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={composeIsAutomatic}
                              onChange={(e) =>
                                onComposeIsAutomaticChanged(e.target.checked)
                              }
                            />
                          }
                          label="Automatic payment (repeating)"
                          sx={{ mb: composeIsAutomatic ? 1 : 0 }}
                        />

                        {composeIsAutomatic && (
                          <Box sx={styles.automaticSectionBox}>
                            <Typography
                              sx={{
                                ...styles.paymentCardMeta,
                                mb: 1.5,
                                opacity: 1,
                                fontWeight: 600,
                              }}
                            >
                              Repeat every
                            </Typography>
                            <TextField
                              fullWidth
                              label="Interval (days)"
                              value={composeIntervalDays}
                              onChange={(e) =>
                                setComposeIntervalDays(e.target.value)
                              }
                              type="number"
                              inputProps={{ min: 1, step: 1 }}
                              helperText="E.g. enter 15 to repeat every 15 days"
                            />
                          </Box>
                        )}

                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={<SendIcon />}
                          onClick={onSubmitPayment}
                          loading={isComposingPayment}
                          disabled={
                            !composeSelectedUser ||
                            !composeAmount ||
                            !composeDate ||
                            !composeDescription.trim() ||
                            (composeIsAutomatic && !composeIntervalDays)
                          }
                          sx={styles.submitButton}
                        >
                          {composeIsAutomatic
                            ? "Schedule Automatic Payment"
                            : "Send Payment"}
                        </Button>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </DrawerAwareContainerComponentView>

      <SnackBarComponentView
        isError={isSnackbarError}
        isOpen={isDisplayingSnackbar}
        message={snackbarMessage}
        onClose={() => setIsDisplayingSnackbar(false)}
      />
    </Box>
  );
};

export default PaymentsScreenView;
